import { useState } from "react";
import { Link } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import Toast from "@/components/base/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import type { NewsItem } from "@/types/content";

type NewsDetailProps = {
  item: NewsItem;
};

export default function NewsDetail({ item }: NewsDetailProps) {
  const [toast, setToast] = useState("");
  const relatedMedia = mediaItems.find((media) => media.id === item.relatedMediaId);
  const otherNews = newsItems.filter((news) => news.id !== item.id).slice(0, 3);

  usePageMeta(
    `${item.title} - 影视资讯 - 天马传媒`,
    `${item.summary} 天马传媒影视资讯，${item.date}发布，分类：${item.category}。`,
    `${item.category},影视资讯,天马传媒,${item.title}`,
  );

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制，可以分享给朋友了");
    } catch {
      setToast("复制失败，请手动复制浏览器地址");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="shell py-6 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link to="/category/news" className="cursor-pointer transition hover:text-primary-600">
              影视资讯
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800">正文</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border border-primary-100 bg-primary-50 px-2 py-1 text-primary-700">
              {item.category}
            </span>
            <span className="text-foreground-500">{item.date}</span>
            <span className="text-foreground-300">·</span>
            <span className="text-foreground-500">热度 {item.hot}</span>
          </div>
          <h1 className="mt-3 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
        </div>
      </div>

      <main className="shell py-8 md:py-10">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px] lg:gap-12">
          <article>
            <div className="aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={item.image}
                alt={`${item.title}配图`}
                title={`${item.title} - 天马传媒影视资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <p className="mt-6 border-l-2 border-accent-500 pl-4 text-sm leading-7 text-foreground-700">
              {item.summary}
            </p>

            <div className="mt-6 space-y-4 text-sm leading-8 text-foreground-800">
              {item.content.map((paragraph) => (
                <p key={paragraph.slice(0, 18)}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
              <p className="text-xs text-foreground-500">
                本文为天马传媒原创资讯演示内容，转载请注明出处。
              </p>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line"></i>
                复制链接
              </button>
            </div>

            {relatedMedia && (
              <div className="mt-6 rounded-lg border border-primary-100 bg-primary-50/60 p-4">
                <p className="text-xs tracking-[0.16em] text-primary-600">相关作品</p>
                <div className="mt-3 flex flex-col gap-4 sm:flex-row">
                  <Link
                    to={`/detail/${relatedMedia.id}`}
                    className="h-36 w-24 shrink-0 overflow-hidden rounded-md bg-primary-900"
                  >
                    <img
                      src={relatedMedia.poster}
                      alt={`${relatedMedia.title}海报`}
                      title={`${relatedMedia.title} - 天马传媒`}
                      className="h-full w-full object-cover object-top"
                    />
                  </Link>
                  <div className="min-w-0">
                    <Link
                      to={`/detail/${relatedMedia.id}`}
                      className="cursor-pointer font-heading text-base font-semibold text-foreground-950 transition hover:text-primary-600"
                    >
                      {relatedMedia.title}
                    </Link>
                    <p className="mt-1 text-xs text-foreground-500">
                      {relatedMedia.year} · {relatedMedia.genres.join(" / ")} · {relatedMedia.rating.toFixed(1)} 分
                    </p>
                    <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-foreground-600">
                      {relatedMedia.summary}
                    </p>
                    <Link
                      to={`/detail/${relatedMedia.id}`}
                      className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs text-primary-600 transition hover:text-accent-600"
                    >
                      查看作品详情
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </article>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="border-b border-background-200 pb-3 font-heading text-base font-semibold text-foreground-950">
                更多资讯
              </h2>
              <ul className="mt-2 divide-y divide-background-200">
                {otherNews.map((news) => (
                  <li key={news.id}>
                    <Link
                      to={`/detail/${news.id}`}
                      className="group block cursor-pointer py-3"
                    >
                      <span className="text-[11px] text-foreground-500">{news.date}</span>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-800 transition group-hover:text-primary-600">
                        {news.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/category/news"
                className="mt-3 flex cursor-pointer items-center justify-center gap-1 rounded-md border border-background-300 py-2 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
              >
                浏览全部资讯
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>

            {relatedMedia && (
              <div className="mt-4">
                <h2 className="mb-3 font-heading text-base font-semibold text-foreground-950">
                  作品速览
                </h2>
                <MediaCard item={relatedMedia} />
              </div>
            )}
          </aside>
        </div>
      </main>

      <Toast message={toast} onClose={() => setToast("")} />
    </div>
  );
}